import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';

import { AssignmentNotice } from './assignment-notice.component';
import { CalibrationAssignmentItem } from './calibration-assignment-item.component';
import { IRecordCompositeScoreInput } from '../../Score/ManageEvaluations/manage-evaluations.interface';
import { formatDate } from '../../../lib/helpers/date.helpers';
import { clamp } from '../../../lib/helpers/number.helpers';
import { useLatestRef } from '../../../lib/hooks/use-latest-ref';
import { CalibrationAssignmentStatus } from '../../../lib/types';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from './my-assignments.messages';
import { groupItemsByClip, nextUnscoredGroupIndex, progressFromGroups } from './assignment-scoring.utils';
import { STAFF_PATHS } from '../../../../_configuration';

interface IAssignmentScoringProps {
  assignment?: ICalibrationAssignment;
  loading: boolean;
  fetchError: boolean;
  initialIndex: number;
  // Clips confirmed unreachable; excluded from scoring entirely (never score a video that can't play).
  unavailableFilenames?: ReadonlySet<string>;
  // Called when a clip fails at playback so the container can exclude it (the HEAD probe can't catch this).
  onVideoUnavailable?: (filename: string) => void;
  compositeScoreHash: { [videoSkillKey: string]: IRecordCompositeScoreInput };
  pendingScoreKeys?: ReadonlySet<string>;
  recordCompositeScore: (op: IRecordCompositeScoreInput) => void;
  removeCompositeScore: (op: IRecordCompositeScoreInput) => void;
  hasUnsavedScores: boolean;
  hasSaveError: boolean;
  isSaving: boolean;
}

export const AssignmentScoring: React.FC<IAssignmentScoringProps> = ({
  assignment,
  loading,
  fetchError,
  initialIndex,
  unavailableFilenames,
  onVideoUnavailable,
  compositeScoreHash,
  pendingScoreKeys,
  recordCompositeScore,
  removeCompositeScore,
  hasUnsavedScores,
  hasSaveError,
  isSaving,
}) => {
  const history = useHistory();
  // Seeded from the container's resume point. The container gates this mount until
  // the buffered scores load, so initialIndex already accounts for offline scores.
  const [index, setIndex] = useState(initialIndex);

  // groupItemsByClip/progressFromGroups handle an undefined assignment (empty array)
  const groups = useMemo(() => groupItemsByClip(assignment, unavailableFilenames), [assignment, unavailableFilenames]);
  // Progress is counted in clips (videos): a clip is scored only when all its skills are.
  const { total, scored } = useMemo(
    () => progressFromGroups(groups, compositeScoreHash),
    [groups, compositeScoreHash],
  );

  // Latest-value refs keep handleClipScored identity-stable (it feeds the skill rows'
  // React.memo through the item's handlers) while reading fresh state at call time.
  const hashRef = useLatestRef(compositeScoreHash);
  const indexRef = useLatestRef(index);

  const goBack = useCallback(() => history.push(STAFF_PATHS.ASSIGNMENTS), [history]);

  // Jump to the next clip still needing a score (wrapping past the end) so the last score
  // never strands them; groups.length = the end screen when none remain.
  const handleClipScored = useCallback(() => {
    const clipCount = groups.length;
    const fromIndex = clamp(indexRef.current, 0, clipCount);
    const next = nextUnscoredGroupIndex(groups, hashRef.current, fromIndex);
    setIndex(next === null ? clipCount : next);
  }, [groups, hashRef, indexRef]);

  // Only blank the scorer when nothing is cached. A failed background refresh under
  // cache-and-network returns the cached assignment AND an error — keep scoring it.
  if (fetchError && !assignment) {
    return <Alert severity="error"><IntlMsg {...messages.scoringLoadError} /></Alert>;
  }

  if (loading && !assignment) {
    return <CircularProgress />;
  }

  if (!assignment) {
    return <AssignmentNotice message={<IntlMsg {...messages.noticeUnavailable} />} onBack={goBack} />;
  }

  if (groups.length === 0) {
    return <AssignmentNotice message={<IntlMsg {...messages.noticeNoVideos} />} onBack={goBack} />;
  }

  if (assignment.status === CalibrationAssignmentStatus.COMPLETED) {
    return (
      <Box>
        <Alert severity="success"><IntlMsg {...messages.completeThanks} values={{ total }} /></Alert>
        <Box mt={2}><Button variant="contained" color="primary" onClick={goBack}><IntlMsg {...messages.backToAssignments} /></Button></Box>
      </Box>
    );
  }

  // total >= 1 below the empty-items return above. The thank-you screen is a virtual
  // page one past the last video (index === total), reached by scoring the final item.
  const allScored = scored === total;
  const safeIndex = clamp(index, 0, total);
  const group = groups[safeIndex];

  const saveStatus = hasSaveError ? (
    <Alert severity="warning"><IntlMsg {...messages.saveErrorRetry} /></Alert>
  ) : isSaving || hasUnsavedScores ? (
    <Chip size="small" label={<IntlMsg {...messages.saving} />} />
  ) : null;

  if (safeIndex >= total) {
    return (
      <Box maxWidth={860} width="100%" mx="auto">
        <Alert severity="success">
          {allScored
            ? <IntlMsg {...messages.thanksAll} values={{ total }} />
            : <IntlMsg {...messages.reachedEnd} values={{ scored, total }} />}
        </Alert>
        {saveStatus && <Box mt={2}>{saveStatus}</Box>}
        <Box mt={2} display="flex" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
          <Button onClick={() => setIndex(total - 1)}><IntlMsg {...messages.goBackLastScore} /></Button>
          <Button variant="contained" color="primary" onClick={goBack}><IntlMsg {...messages.backToAssignments} /></Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxWidth={860} width="100%" mx="auto">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap">
        <Button onClick={goBack}><IntlMsg {...messages.backToList} /></Button>
        <Typography variant="body2" color="textSecondary">
          <IntlMsg {...messages.due} values={{ due: assignment.dueAt ? formatDate(assignment.dueAt) : '—' }} />
        </Typography>
        <Chip size="small" label={<IntlMsg {...messages.scoredCount} values={{ scored, total }} />} />
      </Box>

      {saveStatus && <Box mb={2}>{saveStatus}</Box>}

      <CalibrationAssignmentItem
        key={group.key}
        entry={{ assignment, group }}
        compositeScoreHash={compositeScoreHash}
        pendingScoreKeys={pendingScoreKeys}
        recordCompositeScore={recordCompositeScore}
        removeCompositeScore={removeCompositeScore}
        onScored={handleClipScored}
        onVideoUnavailable={onVideoUnavailable}
      />

      <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
        <Button disabled={safeIndex <= 0} onClick={() => setIndex(safeIndex - 1)}><IntlMsg {...messages.prev} /></Button>
        <Typography variant="body2"><IntlMsg {...messages.videoOfTotal} values={{ current: safeIndex + 1, total }} /></Typography>
        <Button onClick={() => setIndex(safeIndex >= total - 1 ? total : safeIndex + 1)}>
          {safeIndex >= total - 1 ? <IntlMsg {...messages.finish} /> : <IntlMsg {...messages.next} />}
        </Button>
      </Box>
    </Box>
  );
};
